import { ReactNode } from 'react';
import {
    useRecordContext,
    usePermissions,
    useRedirect,
} from 'react-admin';
import {
    Paper,
    Stack,
    Step,
    StepButton,
    Stepper,
    Typography,
} from '@mui/material';

export type WorkflowStep = 0 | 1 | 2 | 3;

export const ChannelWorkflowNav = ({
    step,
    hint,
    action,
    hasResults: hasResultsOverride,
}: {
    step: WorkflowStep;
    hint?: string;
    action?: ReactNode;
    // Pages holding unsaved edits track results themselves
    hasResults?: boolean;
}) => {
    const record = useRecordContext();
    const { permissions } = usePermissions();
    const redirect = useRedirect();

    if (!record) {
        return null;
    }

    const isAdmin = permissions === 'admin';
    const hasBaseline = (record.baseline_values ?? []).length > 0;
    const hasResults = hasResultsOverride ?? (record.integral_results ?? []).length > 0;

    const steps = [
        {
            label: 'Data',
            completed: true,
            disabled: false,
            go: () => redirect('show', 'instrument_channels', record.id),
        },
        {
            label: 'Baseline',
            completed: hasBaseline,
            disabled: !isAdmin,
            go: () => redirect('edit', 'instrument_channels', record.id),
        },
        {
            label: 'Integrate',
            completed: hasResults,
            disabled: !isAdmin || !hasBaseline,
            go: () => redirect(`/instrument_channels/${record.id}/integrate`),
        },
        {
            label: 'Results',
            completed: hasResults,
            disabled: false,
            go: () => redirect('show', 'instruments', record.experiment_id),
        },
    ];

    return (
        <Paper variant="outlined" sx={{ px: 1.5, py: 0.75, mb: 2, width: '100%' }}>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                spacing={2}
            >
                <Stepper
                    nonLinear
                    activeStep={step}
                    sx={{
                        flexGrow: 1,
                        maxWidth: 440,
                        '& .MuiStepIcon-root': { fontSize: 18 },
                        '& .MuiStepLabel-label': { fontSize: '0.8rem' },
                        '& .MuiStepButton-root': { py: 0.5, my: -0.5 },
                    }}
                >
                    {steps.map((s, index) => (
                        <Step key={s.label} completed={index !== step && s.completed}>
                            <StepButton onClick={s.go} disabled={s.disabled}>
                                {s.label}
                            </StepButton>
                        </Step>
                    ))}
                </Stepper>
                {action}
            </Stack>
            {hint && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {hint}
                </Typography>
            )}
        </Paper>
    );
};

export default ChannelWorkflowNav;
